import { useApolloClient } from '@apollo/client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useFlag } from '@openfeature/react-sdk';
import { FEATURE_FLAGS } from '../../constants/constants';
import {
  VULNS_SUMMARY_COUNT,
  KNOWN_EXPLOITS,
  LICENSES,
  CRYPTOGRAPHY_COUNT,
  MISCONFIGURATION_CHECK,
  MISCONFIGURATION_BY_CATEGORY
} from '../../graphql/queries/analytics';
import { VULNERABILITIES } from '../../graphql/queries/vulnerability';
import { DEPENDENCIES } from '../../graphql/queries/dependency';
import { GET_CREDENTIALS } from '../../graphql/queries/credential';
import { HASHES } from '../../graphql/queries/hash';
import { GET_ASSET } from '../../graphql/queries/asset';
import NRAssetSummaryPrintable from './NRAssetSummaryPrintable';

const makeRunKey = (assetId, generateToken) => `${assetId}:${generateToken}`;

function NRAssetSummaryPrintableContainer({ assetId, assetName, generateToken, onComplete, onError }) {
  const client = useApolloClient();
  const printableRef = useRef();
  const lastHandledRunKeyRef = useRef(null);
  const inFlightRunKeyRef = useRef(null);
  const [printData, setPrintData] = useState(null);
  const { value: isComponentIdentifiedViaEnabled } = useFlag(FEATURE_FLAGS.COMPONENT_IDENTIFIED_VIA, false);
  const { value: isComponentConfidenceEnabled } = useFlag(FEATURE_FLAGS.COMPONENT_CONFIDENCE, false);
  const { value: isAiRelatedDependenciesEnabled } = useFlag(FEATURE_FLAGS.AI_RELATED_DEPENDENCIES, false);

  const documentTitle = useMemo(() => `${(assetName || 'asset').replace(/\s+/g, '_')}-Summary`, [assetName]);

  // Latest-callback refs so the `useReactToPrint` lifecycle callbacks always see the
  // current props, even though `useReactToPrint` captures the callbacks once.
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onCompleteRef.current = onComplete;
    onErrorRef.current = onError;
  }, [onComplete, onError]);

  const handlePrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle,
    onAfterPrint: () => {
      setPrintData(null);
      onCompleteRef.current?.();
    },
    onPrintError: (_location, error) => {
      setPrintData(null);
      onErrorRef.current?.(error);
    }
  });

  useEffect(() => {
    if (!generateToken || !assetId) {
      return;
    }
    const runKey = makeRunKey(assetId, generateToken);
    if (lastHandledRunKeyRef.current === runKey) {
      return;
    }
    lastHandledRunKeyRef.current = runKey;
    inFlightRunKeyRef.current = runKey;

    const run = async () => {
      try {
        const analyticsArgs = { args: { composedAssetId: assetId } };
        const v3Context = { version: 'v3' };
        const vulnerabilityArgs = {
          variables: {
            args: {
              assetId,
              cursor: { first: 5 },
              sort: { field: 'EPSSPERCENTILE', order: 'DESC' },
              filter: {
                fields: [
                  {
                    fieldName: 'VULNERABILITYREMEDIATIONSTATUS',
                    value: 'STATUS_AFFECTED&STATUS_UNDER_INVESTIGATION&STATUS_UNSPECIFIED',
                    operation: 'ENUM'
                  }
                ]
              }
            }
          }
        };
        const vulnerabilityThreatActorsArgs = {
          variables: {
            args: {
              assetId,
              cursor: { first: 5 },
              sort: { field: 'SEVERITY', order: 'DESC' },
              filter: {
                inThreatActors: { eq: true },
                fields: [
                  {
                    fieldName: 'MATURITY',
                    value: 'weaponized',
                    operation: 'CONTAINS'
                  },
                  {
                    fieldName: 'VULNERABILITYREMEDIATIONSTATUS',
                    value: 'STATUS_AFFECTED&STATUS_UNDER_INVESTIGATION&STATUS_UNSPECIFIED',
                    operation: 'ENUM'
                  }
                ]
              }
            }
          }
        };
        const dependenciesTopFiveVulnCountArgs = {
          variables: {
            args: {
              composedAssetId: assetId,
              cursor: { first: 5 },
              sort: { field: 'VULNERABILITY', order: 'DESC' },
              showOriginalState: false
            },
            includeIdentifiedVia: isComponentIdentifiedViaEnabled,
            includeConfidence: isComponentConfidenceEnabled,
            includeAiRelatedDependencies: isAiRelatedDependenciesEnabled
          }
        };
        const crackedCredentialsArgs = {
          variables: {
            args: {
              assetId,
              filter: { fields: [{ fieldName: 'CRACKED', value: 'True', operation: 'EQUAL' }] },
              cursor: { first: 50 }
            }
          }
        };

        const safeQuery = (promise, fallback) => promise.catch(() => fallback);

        const [
          assetRes,
          vulnRes,
          exploitsRes,
          cryptoRes,
          misconfigRes,
          misconfigCatRes,
          licensesRes,
          vulnerabilitiesByEPSS,
          vulnerabilitiesThreatActors,
          dependenciesTopFiveVulnCount,
          crackedCredentialsUsers,
          crackedCredentialsHashes
        ] = await Promise.all([
          safeQuery(client.query({ query: GET_ASSET, fetchPolicy: 'no-cache', variables: { args: { assetId } }, context: v3Context }), {
            data: { asset: null }
          }),
          safeQuery(client.query({ query: VULNS_SUMMARY_COUNT, fetchPolicy: 'no-cache', variables: analyticsArgs, context: v3Context }), {
            data: { analytics: { vulnerabilitiesSummaryCount: {} } }
          }),
          safeQuery(client.query({ query: KNOWN_EXPLOITS, fetchPolicy: 'no-cache', variables: analyticsArgs, context: v3Context }), {
            data: { analytics: { knownExploits: {} } }
          }),
          safeQuery(client.query({ query: CRYPTOGRAPHY_COUNT, fetchPolicy: 'no-cache', variables: analyticsArgs, context: v3Context }), {
            data: { analytics: { cryptographyCount: {} } }
          }),
          safeQuery(client.query({ query: MISCONFIGURATION_CHECK, fetchPolicy: 'no-cache', variables: analyticsArgs, context: v3Context }), {
            data: { analytics: { misconfigurationCheck: {} } }
          }),
          safeQuery(client.query({ query: MISCONFIGURATION_BY_CATEGORY, fetchPolicy: 'no-cache', variables: analyticsArgs, context: v3Context }), {
            data: { analytics: { misconfigurationByCategory: [] } }
          }),
          safeQuery(client.query({ query: LICENSES, fetchPolicy: 'no-cache', variables: analyticsArgs, context: v3Context }), {
            data: { analytics: { licenses: [] } }
          }),
          safeQuery(client.query({ query: VULNERABILITIES, fetchPolicy: 'no-cache', variables: vulnerabilityArgs.variables, context: v3Context }), {
            data: { vulnerabilities: { edges: [] } }
          }),
          safeQuery(
            client.query({ query: VULNERABILITIES, fetchPolicy: 'no-cache', variables: vulnerabilityThreatActorsArgs.variables, context: v3Context }),
            { data: { vulnerabilities: { edges: [] } } }
          ),
          safeQuery(
            client.query({ query: DEPENDENCIES, fetchPolicy: 'no-cache', variables: dependenciesTopFiveVulnCountArgs.variables, context: v3Context }),
            { data: { dependencies: { edges: [] } } }
          ),
          safeQuery(
            client.query({ query: GET_CREDENTIALS, fetchPolicy: 'no-cache', variables: crackedCredentialsArgs.variables, context: v3Context }),
            {
              data: { credentials: { edges: [] } }
            }
          ),
          safeQuery(client.query({ query: HASHES, fetchPolicy: 'no-cache', variables: crackedCredentialsArgs.variables, context: v3Context }), {
            data: { hashes: { edges: [] } }
          })
        ]);

        const topVulnsByEPSS = (vulnerabilitiesByEPSS?.data?.vulnerabilities?.edges || []).map(({ node }) => ({
          cve: node?.cve,
          epssPercentile: node?.epssPercentile,
          vendor: node?.vendor,
          version: node?.version
        }));

        const topThreatActorWeaponized = (vulnerabilitiesThreatActors?.data?.vulnerabilities?.edges || []).map(({ node }) => ({
          cve: node?.cve,
          severity: node?.severity,
          vendor: node?.vendor,
          version: node?.version
        }));

        const topVulnerableComponents = (dependenciesTopFiveVulnCount?.data?.dependencies?.edges || []).map(({ node }) => {
          const vulnerability = node?.analytic?.vulnerability || {};
          const total = (vulnerability.critical || 0) + (vulnerability.high || 0) + (vulnerability.medium || 0) + (vulnerability.low || 0);
          return {
            name: node?.dependency?.name,
            vendor: node?.dependency?.vendor,
            version: node?.dependency?.version?.id,
            vulnerabilities: {
              ...vulnerability,
              total
            }
          };
        });

        const crackedCredentials = [
          ...(crackedCredentialsUsers?.data?.credentials?.edges || []).map(({ node }) => ({
            type: 'User Account',
            userName: node?.userName,
            filePath: node?.filePath
          })),
          ...(crackedCredentialsHashes?.data?.hashes?.edges || []).map(({ node }) => ({
            type: 'Detected Hash',
            hash: node?.hash,
            filePath: node?.filePath
          }))
        ].filter(cred => !!cred.filePath || !!cred.userName || !!cred.hash);

        const misconfigurationCheck = misconfigRes?.data?.analytics?.misconfigurationCheck;

        // Drop the result if a newer generation has started while we were awaiting.
        if (inFlightRunKeyRef.current !== runKey) {
          return;
        }

        setPrintData({
          asset: assetRes?.data?.asset,
          vulnSummary: vulnRes?.data?.analytics?.vulnerabilitiesSummaryCount || {},
          exploits: exploitsRes?.data?.analytics?.knownExploits || {},
          crypto: cryptoRes?.data?.analytics?.cryptographyCount || {},
          misconfig: misconfigurationCheck || {},
          misconfigByCat: misconfigCatRes?.data?.analytics?.misconfigurationByCategory || [],
          topVulnsByEPSS,
          topThreatActorWeaponized,
          topVulnerableComponents,
          licenses: licensesRes?.data?.analytics?.licenses || [],
          crackedCredentials,
          generatedAt: new Date().toLocaleString()
        });
      } catch (error) {
        if (inFlightRunKeyRef.current !== runKey) {
          return;
        }
        onError?.(error);
      }
    };

    run();
  }, [generateToken, assetId, client, isComponentIdentifiedViaEnabled, isComponentConfidenceEnabled, isAiRelatedDependenciesEnabled, onError]);

  useEffect(() => {
    if (!printData) {
      return;
    }

    // Wait two animation frames so React has committed the print subtree and the
    // browser has run a layout pass — Apex/Highcharts both size their SVG on first
    // paint, so triggering print before that yields blank/partial pages.
    let frame2 = null;
    const frame1 = requestAnimationFrame(() => {
      frame2 = requestAnimationFrame(() => {
        try {
          handlePrint();
        } catch (error) {
          setPrintData(null);
          onErrorRef.current?.(error);
        }
      });
    });

    return () => {
      cancelAnimationFrame(frame1);
      if (frame2 !== null) cancelAnimationFrame(frame2);
    };
  }, [printData, handlePrint]);

  if (!printData) {
    return null;
  }

  return <NRAssetSummaryPrintable ref={printableRef} {...printData} />;
}

export default NRAssetSummaryPrintableContainer;
